import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  ClipboardList,
  BarChart3,
  Users,
  LogOut,
  User,
  Settings,
  ChevronDown,
  Activity,
  FileText,
  LayoutGrid,
  Loader2,
  UserPlus,
  TrendingUp,
  PieChart,
  Target
} from 'lucide-react';
import { authAPI, dashboardAPI, surveysAPI } from '../services/api';
import { MobileNavAdmin } from '../components/MobileNav';

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      const statsData = await dashboardAPI.getStats();
      setStats(statsData);
      const surveysData = await surveysAPI.getAll();
      setSurveys(surveysData);
    } catch (error) {
      if (error.response?.status === 401) navigate('/auth');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await authAPI.logout();
    navigate('/auth');
  };

  const isAdmin = user?.role === 'administrator';

  const surveysByType = surveys.reduce((acc, s) => {
    acc[s.type] = (acc[s.type] || 0) + 1;
    return acc;
  }, {});

  const surveysByStatus = surveys.reduce((acc, s) => {
    acc[s.status] = (acc[s.status] || 0) + 1;
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-slate-900 border-r border-slate-800 z-40 hidden lg:flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">KAP<span className="text-cyan-400">Surv</span></span>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <button onClick={() => navigate('/dashboard')} className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-colors">
            <LayoutGrid className="w-5 h-5" /> Dashboard
          </button>
          <button onClick={() => navigate('/surveys')} className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-colors">
            <ClipboardList className="w-5 h-5" /> Surveys
          </button>
          <button className="w-full flex items-center gap-3 px-4 py-3 text-white bg-white/10 rounded-xl font-medium">
            <BarChart3 className="w-5 h-5" /> Analytics
          </button>
          {isAdmin && (
            <button onClick={() => navigate('/users')} className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-colors">
              <UserPlus className="w-5 h-5" /> Manage Users
            </button>
          )}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white font-semibold">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate">{isAdmin ? 'Administrator' : user?.role}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-800">
              <DropdownMenuItem className="gap-2 text-slate-300"><User className="w-4 h-4" /> Profile</DropdownMenuItem>
              <DropdownMenuItem className="gap-2 text-slate-300"><Settings className="w-4 h-4" /> Settings</DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuItem className="gap-2 text-red-400" onClick={handleLogout}><LogOut className="w-4 h-4" /> Log out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64 p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Analytics</h1>
          <p className="text-slate-400">Overview of your survey performance and insights</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Total Surveys</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats?.total_surveys || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center">
                  <ClipboardList className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Total Responses</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats?.total_responses?.toLocaleString() || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-emerald-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Active Studies</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats?.active_studies || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-purple-500/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Avg Completion</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats?.completion_rate || 0}%</p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center">
                  <Target className="w-6 h-6 text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2"><PieChart className="w-5 h-5 text-cyan-400" /> Surveys by Type</CardTitle>
              <CardDescription className="text-slate-400">Distribution of survey types</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(surveysByType).length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No data available</p>
                ) : (
                  Object.entries(surveysByType).map(([type, count]) => {
                    const total = surveys.length;
                    const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                    const colors = {
                      kap: { bg: 'bg-emerald-500', text: 'KAP Survey' },
                      mrs: { bg: 'bg-blue-500', text: 'Market Research' },
                      patient: { bg: 'bg-purple-500', text: 'Patient Survey' },
                      hcp: { bg: 'bg-amber-500', text: 'HCP Feedback' }
                    };
                    const color = colors[type] || { bg: 'bg-slate-500', text: type };
                    return (
                      <div key={type}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-300">{color.text}</span>
                          <span className="text-slate-400">{count} ({percentage}%)</span>
                        </div>
                        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                          <div className={`h-full ${color.bg} rounded-full`} style={{ width: `${percentage}%` }} />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2"><BarChart3 className="w-5 h-5 text-cyan-400" /> Surveys by Status</CardTitle>
              <CardDescription className="text-slate-400">Current status distribution</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(surveysByStatus).length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No data available</p>
                ) : (
                  Object.entries(surveysByStatus).map(([status, count]) => {
                    const total = surveys.length;
                    const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                    const colors = {
                      active: { bg: 'bg-emerald-500', text: 'Active' },
                      draft: { bg: 'bg-slate-500', text: 'Draft' },
                      completed: { bg: 'bg-blue-500', text: 'Completed' },
                      archived: { bg: 'bg-amber-500', text: 'Archived' }
                    };
                    const color = colors[status] || { bg: 'bg-slate-500', text: status };
                    return (
                      <div key={status}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-300">{color.text}</span>
                          <span className="text-slate-400">{count} ({percentage}%)</span>
                        </div>
                        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                          <div className={`h-full ${color.bg} rounded-full`} style={{ width: `${percentage}%` }} />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Performing Surveys */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">Top Performing Surveys</CardTitle>
            <CardDescription className="text-slate-400">Surveys with highest response rates</CardDescription>
          </CardHeader>
          <CardContent>
            {surveys.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No surveys available</p>
            ) : (
              <div className="space-y-4">
                {surveys
                  .sort((a, b) => b.completion_rate - a.completion_rate)
                  .slice(0, 5)
                  .map((survey, index) => (
                    <div 
                      key={survey.id} 
                      onClick={() => navigate(`/surveys/${survey.id}/analytics`)}
                      className="flex items-center gap-4 p-4 bg-slate-800/50 rounded-xl cursor-pointer hover:bg-slate-800 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center text-slate-300 font-bold">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium">{survey.title}</p>
                        <p className="text-sm text-slate-500">{survey.responses} responses</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-cyan-400">{Math.round(survey.completion_rate)}%</p>
                        <p className="text-xs text-slate-500">completion</p>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileNavAdmin />
      
      {/* Bottom Padding for Mobile Nav */}
      <div className="lg:hidden h-24" />
    </div>
  );
};

export default AnalyticsPage;
